import { CommonModule } from '@angular/common';
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { ITableColumnSetting } from '../../shared/table-preferences.service';

/**
 * One configurable section inside the drawer. `prefsKey` is the
 * `TablePreferencesService` key the section's visibility/order is stored under,
 * so two surfaces rendering the same sections stay in sync.
 */
export interface IDetailFieldGroup {
  key: string;
  /** Section heading (i18n key). */
  title: string;
  prefsKey: string;
  fields: ITableColumnSetting[];
}

/** Emitted whenever one section's fields are reordered or toggled. */
export interface IDetailFieldGroupChange {
  group: IDetailFieldGroup;
  columns: ITableColumnSetting[];
}

/**
 * Single slide-over that configures every configurable block of a detail panel
 * at once (WB-7463). One trigger, one drawer, one section per block — rather
 * than a settings icon repeated in every section header.
 *
 * Persistence stays with the host: this component only emits the new ordered
 * list for whichever section changed.
 */
@Component({
  selector: 'ui-detail-fields-settings',
  standalone: true,
  imports: [CommonModule, DragDropModule, TranslateModule, SvgIconComponent],
  templateUrl: './detail-fields-settings.component.html',
  styleUrls: ['./detail-fields-settings.component.scss'],
})
export class DetailFieldsSettingsComponent {
  @Input() groups: IDetailFieldGroup[] = [];

  /** Svg icon name for the trigger; matches every table's column settings. */
  @Input() triggerIcon = 'sliders-horizontal';

  @Output() groupChange = new EventEmitter<IDetailFieldGroupChange>();

  open = false;

  toggle(): void {
    this.open = !this.open;
  }

  @HostListener('document:keydown.escape')
  close(): void {
    this.open = false;
  }

  visibleOf(group: IDetailFieldGroup): ITableColumnSetting[] {
    return group.fields.filter((field) => field.visible);
  }

  hiddenOf(group: IDetailFieldGroup): ITableColumnSetting[] {
    return group.fields.filter((field) => !field.visible);
  }

  /** A field can be hidden unless explicitly flagged otherwise. */
  isHideable(field: ITableColumnSetting): boolean {
    return field.hideable !== false;
  }

  onDrop(group: IDetailFieldGroup, event: CdkDragDrop<ITableColumnSetting[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    const visible = this.visibleOf(group);
    moveItemInArray(visible, event.previousIndex, event.currentIndex);
    this.emit(group, [...visible, ...this.hiddenOf(group)]);
  }

  hide(group: IDetailFieldGroup, field: ITableColumnSetting): void {
    if (!this.isHideable(field)) {
      return;
    }
    this.emit(group, [
      ...this.visibleOf(group).filter((item) => item.key !== field.key),
      ...this.hiddenOf(group),
      { ...field, visible: false },
    ]);
  }

  show(group: IDetailFieldGroup, field: ITableColumnSetting): void {
    this.emit(group, [
      ...this.visibleOf(group),
      { ...field, visible: true },
      ...this.hiddenOf(group).filter((item) => item.key !== field.key),
    ]);
  }

  private emit(group: IDetailFieldGroup, columns: ITableColumnSetting[]): void {
    this.groupChange.emit({ group, columns });
  }

  trackByKey(_index: number, field: ITableColumnSetting): string {
    return field.key;
  }
}
